
import React, { useState, useMemo } from "react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { IDDocumentType } from "@/hooks/admin/identity-docs/useIDTypes";

interface DocumentTypeSelectorProps {
  form: UseFormReturn<any>;
  filteredIdTypes: IDDocumentType[];
  onDocumentTypeChange: (value: string) => void;
  sectionIndex: number;
}

const DocumentTypeSelector = ({
  form,
  filteredIdTypes,
  onDocumentTypeChange,
  sectionIndex,
}: DocumentTypeSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const searchFilteredTypes = useMemo(() => {
    if (!searchTerm) return filteredIdTypes;
    
    return filteredIdTypes.filter(type => 
      type.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.country_code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [filteredIdTypes, searchTerm]);

  // Auto-select document type when only one is available
  React.useEffect(() => {
    if (filteredIdTypes.length === 1 && form.getValues(`sections.${sectionIndex}.country_code`)) {
      const currentValue = form.getValues(`sections.${sectionIndex}.document_type_id`);
      if (!currentValue) {
        form.setValue(`sections.${sectionIndex}.document_type_id`, filteredIdTypes[0].id);
        onDocumentTypeChange(filteredIdTypes[0].id);
      }
    }
  }, [filteredIdTypes, form, sectionIndex, onDocumentTypeChange]);

  return (
    <FormField
      control={form.control}
      name={`sections.${sectionIndex}.document_type_id`}
      render={({ field }) => (
        <FormItem>
          <FormLabel>ID Document Type</FormLabel>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              onDocumentTypeChange(value);
            }}
            value={field.value}
            disabled={
              !form.getValues(`sections.${sectionIndex}.country_code`) || 
              filteredIdTypes.length === 0
            }
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !form.getValues(`sections.${sectionIndex}.country_code`)
                      ? "Select country first"
                      : filteredIdTypes.length === 0
                      ? "No document types available"
                      : filteredIdTypes.length === 1
                      ? filteredIdTypes[0].document_name
                      : "Select ID type"
                  }
                />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {filteredIdTypes.length > 1 && (
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search document types..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
              )}
              <div className="max-h-[200px] overflow-y-auto">
                {searchFilteredTypes.length > 0 ? (
                  searchFilteredTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex flex-col">
                        <span>{type.document_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {[
                            type.proves_citizenship ? "Citizenship" : "",
                            type.proves_residency ? "Residency" : "",
                            type.global_document ? "Global" : "",
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-results" disabled>
                    {searchTerm ? "No document types found" : "No document types available"}
                  </SelectItem>
                )}
              </div>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default DocumentTypeSelector;
